import { useEffect, useState } from "react";
import type { ImageObject as CommerceImageObject } from "@decocms/apps-commerce/types";
import type { Platform } from "~/apps/site";
import { formatPrice } from "@decocms/apps-commerce/sdk/formatPrice";
import { getProductImageAlt } from "~/utils/product";

interface OffersObject {
  priceCurrency?: string;
}

export interface Props {
  isAvailable: boolean;
  platform: Platform;
  images?: CommerceImageObject[] | null;
  listPrice: number;
  price?: number;
  productName?: string;
  offers?: OffersObject;
}

function FloatBuyButton(
  { isAvailable, platform, listPrice, price, productName, images, offers }:
    Props,
) {
  const [staticButtonVisible, setStaticButtonVisible] = useState(false);
  const [atPageBottom, setAtPageBottom] = useState(false);
  const productImages = images ?? [];
  const currentPrice = price ?? 0;

  const checkVisibilityAndUpdateState = () => {
    const buyButton = document.querySelector("#buyButton");
    if (buyButton) {
      const visible = checkVisible(buyButton);
      if (staticButtonVisible !== visible) {
        setStaticButtonVisible(visible);
      }
    }

    const bottom = globalThis.scrollY + globalThis.innerHeight >=
      document.documentElement.scrollHeight - 1;
    if (atPageBottom !== bottom) {
      setAtPageBottom(bottom);
    }
  };

  useEffect(() => {
    checkVisibilityAndUpdateState();

    globalThis.addEventListener("scroll", checkVisibilityAndUpdateState);
    return () => {
      globalThis.removeEventListener("scroll", checkVisibilityAndUpdateState);
    };
  }, [staticButtonVisible, atPageBottom]);

  return (
    <>
      {isAvailable && !staticButtonVisible && !atPageBottom &&
        platform === "vtex" && (
        <div
          id="floatBuyButton"
          className="flex justify-between z-20 fixed bottom-0 left-0 right-0 px-8 lg:px-0 py-3 lg:py-2 bg-white"
        >
          <div className="flex justify-between w-full max-w-[1280px] m-auto">
            <div className="hidden lg:flex items-center gap-4">
              {productImages.length > 0 && (
                <img
                  width={64}
                  height={68}
                  src={productImages[0].url}
                  alt={getProductImageAlt(productName, 0)}
                  loading="lazy"
                  decoding="async"
                />
              )}
              <p className="flex items-center m-0 font-['Poppins','Times_New_Roman',Times,serif] not-italic font-normal text-[18.5px] leading-[25.9px] text-[#656565]">
                {productName}
              </p>
            </div>

            <div className="flex w-full lg:w-auto items-center gap-24">
              <div className="hidden lg:flex items-center flex-col">
                <p className="m-0 line-through font-['Poppins','Times_New_Roman',Times,serif] font-normal text-base leading-[19.2px] text-[#4c4c4c]">
                  {(listPrice ?? 0) > currentPrice &&
                    formatPrice(listPrice, offers?.priceCurrency)}
                </p>
                <p className="m-0 font-['Poppins','Times_New_Roman',Times,serif] font-normal text-lg leading-[21.6px]">
                  {formatPrice(currentPrice, offers?.priceCurrency)}
                </p>
              </div>
              <button
                className="flex justify-center items-center uppercase bg-[#1A1A1A] text-white w-full h-14 lg:max-w-[353px] lg:h-12 py-5 px-[35px] leading-[18px] text-base font-['Poppins',Helvetica,Arial,sans-serif] font-semibold order-1 transition-[background-color] duration-[0.2s] ease-linear hover:bg-[#C18A58] hover:text-[#1A1A1A] lg:w-[341px]"
                onClick={() => {
                  const buyButton = document.querySelector<HTMLElement>(
                    "#buyButton",
                  );
                  if (buyButton) {
                    const scrollPosition = globalThis.scrollY;
                    const isDesktop = globalThis.innerWidth >= 1024;
                    const targetPosition = scrollPosition === 0
                      ? buyButton.offsetTop - 400
                      : buyButton.offsetTop - (isDesktop ? 600 : 290);

                    globalThis.scrollTo({
                      top: targetPosition,
                      behavior: "smooth",
                    });

                    // Sem isto o clique não dava nenhum retorno visual --
                    // a página rolava e parava, e sem destaque nenhum no
                    // destino parecia que o clique tinha falhado (achado 07
                    // da auditoria de 23/08/2026).
                    buyButton.classList.add("buy-button-highlight");
                    setTimeout(() => {
                      buyButton.classList.remove("buy-button-highlight");
                    }, 1400);
                  }
                }}
              >
                COMPRAR
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function checkVisible(elm: Element) {
  const rect = elm.getBoundingClientRect();
  const viewHeight = Math.max(
    document.documentElement.clientHeight,
    globalThis.innerHeight,
  );
  return !(rect.bottom < 0 || rect.top - viewHeight >= 0);
}

export default FloatBuyButton;
